/**
 * <SendDealLinkModal> — `external-sharing` Phase 3 Session 3.2 (2026-05-09).
 *
 * Owner-side share-creation form. Mounts at `<BuilderPage>` root + listens for
 * the `arcanum:send-deal-link` window event (Phase 6 already dispatches from
 * <SendDealLinkButton>). On submit, calls `POST /api/shares` via the shared
 * `createShare` wrapper. Three terminal states:
 *
 *   - SUCCESS  — copy-link panel showing `${APP_URL}/deal/<shareId>` + a
 *                [Copy link] button (uses `navigator.clipboard.writeText`).
 *   - COLLISION (409 SHARE_DUPLICATE_RECIPIENT) — "already shared" panel
 *                pointing at `details.existingId` from the error envelope.
 *                User can copy the existing link OR navigate to the existing
 *                share row in `/shares?tab=sent`.
 *   - ERROR    — generic 4xx/5xx surface with a [Retry] action.
 *
 * Built as a fresh modal shell (NOT on `<ConfirmModal>`, which is a binary
 * confirm/cancel dialog — not a multi-state form host). Replicates the
 * dialog backdrop pattern from `<ConfirmModal>` for visual consistency:
 * `fixed inset-0 bg-void/70 z-50` overlay + centered panel.
 *
 * Spec §5. Acceptance criteria:
 *   - Opens on `arcanum:send-deal-link` window event.
 *   - Email validated client-side (regex).
 *   - Message capped at 500 chars.
 *   - Submit → POST /api/shares → success state w/ copy-link.
 *   - 409 collision → "already shared" UX routes to existing share.
 *   - Esc / [Cancel] / click-outside → closes modal + resets state.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { ApiError } from '@/lib/api';
import { buildShareUrl, createShare } from '@/lib/shares-api';
import { posthog } from '@/lib/posthog';

// ----------------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------------

/** RFC-5322-ish enough — accepts the common 99% case + rejects obvious typos.
 *  Server-side Zod (.email()) is the authoritative check; client-side is a
 *  pre-flight UX gate. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MESSAGE_MAX_LEN = 500;

// `buildShareUrl` is imported from `@/lib/shares-api` so this component +
// `<SentShareRow>` stay in sync on the URL shape. Single source of truth for
// the public deal-link URL pattern.

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

export interface SendDealLinkModalProps {
  /** Conversation the modal is bound to. When null, the modal won't open
   *  even if the window event fires (no deal to share). */
  conversationId: string | null;
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; shareId: string }
  | { kind: 'collision'; existingShareId: string }
  | { kind: 'error'; code?: string; message: string };

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export function SendDealLinkModal({ conversationId }: SendDealLinkModalProps) {
  const [open, setOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [copied, setCopied] = useState(false);
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = useCallback(() => {
    setRecipientEmail('');
    setMessage('');
    setEmailError(null);
    setPhase({ kind: 'idle' });
    setCopied(false);
    if (copyResetTimer.current) {
      clearTimeout(copyResetTimer.current);
      copyResetTimer.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    reset();
  }, [reset]);

  // Listen for the `arcanum:send-deal-link` window event dispatched by
  // <SendDealLinkButton>. Guard on conversationId match so cross-route mounts
  // (if any future page also dispatches the event for a different conversation)
  // don't trigger the wrong modal.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e: Event): void => {
      const detail = (e as CustomEvent<{ conversationId?: string }>).detail;
      if (!detail?.conversationId) return;
      if (conversationId === null) return;
      if (detail.conversationId !== conversationId) return;
      setOpen(true);
    };
    window.addEventListener('arcanum:send-deal-link', handler);
    return () => window.removeEventListener('arcanum:send-deal-link', handler);
  }, [conversationId]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, handleClose]);

  // Cleanup copy-reset timer on unmount.
  useEffect(() => {
    return () => {
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    };
  }, []);

  if (!open) return null;
  if (conversationId === null) return null;

  const messageCount = message.length;

  const handleSubmit = async (): Promise<void> => {
    setEmailError(null);
    if (!EMAIL_RE.test(recipientEmail.trim())) {
      setEmailError('Enter a valid email address.');
      return;
    }
    if (messageCount > MESSAGE_MAX_LEN) {
      // Defensive — Textarea maxLength already prevents this, but server
      // would reject too. Keep the surface honest.
      setPhase({
        kind: 'error',
        message: `Message must be ${MESSAGE_MAX_LEN} characters or fewer.`,
      });
      return;
    }
    setPhase({ kind: 'submitting' });
    try {
      const share = await createShare({
        conversationId,
        recipientEmail: recipientEmail.trim(),
        message: message.trim().length > 0 ? message.trim() : undefined,
      });
      // NEW Phase 3 § 3.4 — PostHog product event: share created.
      posthog.capture('share_created', { share_id: share.id });
      setPhase({ kind: 'success', shareId: share.id });
    } catch (err) {
      // Server emits `code: 'SHARE_ALREADY_PENDING_OR_ACTIVE'` with
      // `details.existingShareId` for the partial-UNIQUE collision (verified
      // against /api/shares 409 response body, 2026-05-09). The shared-types
      // doc references `SHARE_DUPLICATE_RECIPIENT` as a planned alias but
      // the actual factory in `apps/api/src/modules/sharing/sharing.errors.ts`
      // uses the longer name. Match what the wire actually sends.
      if (
        err instanceof ApiError &&
        (err.code === 'SHARE_ALREADY_PENDING_OR_ACTIVE' ||
          err.code === 'SHARE_DUPLICATE_RECIPIENT')
      ) {
        const details = err.details as
          | { existingShareId?: string; existingId?: string }
          | undefined;
        const existingShareId = details?.existingShareId ?? details?.existingId;
        if (existingShareId) {
          setPhase({ kind: 'collision', existingShareId });
          return;
        }
      }
      const code = err instanceof ApiError ? err.code : undefined;
      const friendly =
        err instanceof ApiError && err.code === 'INSUFFICIENT_TIER'
          ? 'Only approved users can create shares.'
          : err instanceof ApiError && err.status >= 500
          ? 'Something went wrong on our end. Try again or contact support.'
          : err instanceof Error
          ? err.message
          : 'Failed to create share.';
      setPhase({ kind: 'error', code, message: friendly });
    }
  };

  const handleCopy = async (shareId: string): Promise<void> => {
    const url = buildShareUrl(shareId);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
      copyResetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail in non-secure contexts or without user
      // gesture; surface a quiet warning rather than crashing the modal.
      console.warn('[SendDealLinkModal] clipboard.writeText failed');
    }
  };

  const submitting = phase.kind === 'submitting';
  const showForm =
    phase.kind === 'idle' ||
    phase.kind === 'submitting' ||
    phase.kind === 'error';

  return (
    <>
      <div
        data-testid="send-deal-link-modal-overlay"
        className="fixed inset-0 bg-void/70 z-50"
        onClick={handleClose}
      />
      <div
        data-testid="send-deal-link-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-deal-link-modal-title"
        className={clsx(
          'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
          'w-full max-w-lg bg-panel-bg border border-panel-border rounded-[3px]',
          'shadow-[0_8px_24px_rgba(0,0,0,0.4)]'
        )}
      >
        <div className="px-5 py-3 border-b border-panel-border flex items-center justify-between">
          <span
            id="send-deal-link-modal-title"
            className="text-xs font-mono font-semibold text-text-muted tracking-[1.4px] uppercase"
          >
            Send Deal Link
          </span>
          <button
            type="button"
            data-testid="send-deal-link-modal-close"
            onClick={handleClose}
            aria-label="Close"
            className="text-text-dim hover:text-text-bright text-base leading-none cursor-pointer"
          >
            ✕
          </button>
        </div>

        {showForm && (
          <div className="p-5 flex flex-col gap-4">
            <div>
              <label
                htmlFor="send-deal-link-modal-email"
                className="block text-xs text-text-muted font-mono uppercase tracking-[0.6px] mb-1.5"
              >
                Recipient email
              </label>
              <Input
                id="send-deal-link-modal-email"
                data-testid="send-deal-link-modal-email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="alice@example.com"
                disabled={submitting}
                autoFocus
              />
              {emailError && (
                <p
                  data-testid="send-deal-link-modal-email-error"
                  className="text-xs font-mono text-danger mt-1.5"
                >
                  {emailError}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="send-deal-link-modal-message"
                className="block text-xs text-text-muted font-mono uppercase tracking-[0.6px] mb-1.5"
              >
                Message{' '}
                <span className="text-text-dim normal-case tracking-normal">
                  (optional)
                </span>
              </label>
              <Textarea
                id="send-deal-link-modal-message"
                data-testid="send-deal-link-modal-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Take a look at this deal…"
                maxLength={MESSAGE_MAX_LEN}
                rows={3}
                disabled={submitting}
              />
              <div className="flex justify-end mt-1.5">
                <span
                  data-testid="send-deal-link-modal-message-count"
                  className={clsx(
                    'text-xs font-mono tabular-nums',
                    messageCount >= MESSAGE_MAX_LEN
                      ? 'text-warn'
                      : 'text-text-dim'
                  )}
                >
                  {messageCount}/{MESSAGE_MAX_LEN}
                </span>
              </div>
            </div>

            {phase.kind === 'error' && (
              <div
                data-testid="send-deal-link-modal-error"
                className="px-3 py-2 border border-danger/15 bg-danger-dim rounded-[2px] text-sm font-mono text-danger"
              >
                {phase.message}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-panel-border -mx-5 px-5 pb-0">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={submitting}
                data-testid="send-deal-link-modal-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleSubmit()}
                disabled={submitting || recipientEmail.trim().length === 0}
                data-testid="send-deal-link-modal-submit"
              >
                {submitting ? 'Sending…' : 'Send link'}
              </Button>
            </div>
          </div>
        )}

        {phase.kind === 'success' && (
          <CopyLinkPanel
            kind="success"
            shareId={phase.shareId}
            url={buildShareUrl(phase.shareId)}
            copied={copied}
            onCopy={() => void handleCopy(phase.shareId)}
            onDone={handleClose}
          />
        )}

        {phase.kind === 'collision' && (
          <CopyLinkPanel
            kind="collision"
            shareId={phase.existingShareId}
            url={buildShareUrl(phase.existingShareId)}
            copied={copied}
            onCopy={() => void handleCopy(phase.existingShareId)}
            onDone={handleClose}
          />
        )}
      </div>
    </>
  );
}

// ----------------------------------------------------------------------------
// CopyLinkPanel — shared between success + collision paths.
// ----------------------------------------------------------------------------

interface CopyLinkPanelProps {
  kind: 'success' | 'collision';
  shareId: string;
  url: string;
  copied: boolean;
  onCopy: () => void;
  onDone: () => void;
}

function CopyLinkPanel({
  kind,
  shareId,
  url,
  copied,
  onCopy,
  onDone,
}: CopyLinkPanelProps) {
  const headline = kind === 'success' ? 'SHARE CREATED' : 'ALREADY SHARED';
  const description =
    kind === 'success'
      ? 'Copy the link below and send it to your recipient.'
      : "You've already shared this deal with that recipient. Copy the existing link or open it in your Sent tab.";

  return (
    <div
      data-testid={
        kind === 'success'
          ? 'send-deal-link-modal-success'
          : 'send-deal-link-modal-collision'
      }
      className="p-5 flex flex-col gap-4"
    >
      <div>
        <span className="text-xs font-mono font-semibold text-accent tracking-[0.6px] uppercase">
          {headline}
        </span>
        <p className="text-sm font-body text-text mt-1 leading-relaxed">
          {description}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <input
          data-testid="send-deal-link-modal-copy-input"
          readOnly
          value={url}
          className={clsx(
            'flex-1 py-2.5 px-3 bg-input-bg border border-input-border rounded-[3px]',
            'text-text-bright text-sm font-mono outline-none',
            'select-all'
          )}
        />
        <Button
          variant="primary"
          onClick={onCopy}
          data-testid="send-deal-link-modal-copy-button"
        >
          {copied ? 'Copied' : 'Copy link'}
        </Button>
      </div>
      <div className="flex justify-between items-center pt-2 border-t border-panel-border -mx-5 px-5 pb-0">
        {kind === 'collision' ? (
          <Link
            to="/shares"
            search={{ tab: 'sent' as const, highlight: shareId }}
            onClick={onDone}
            data-testid="send-deal-link-modal-view-existing"
            className="text-xs font-mono text-text-muted hover:text-text-bright underline-offset-2 hover:underline"
          >
            View existing share →
          </Link>
        ) : (
          <span />
        )}
        <Button
          variant="secondary"
          onClick={onDone}
          data-testid="send-deal-link-modal-done"
        >
          Done
        </Button>
      </div>
    </div>
  );
}
